import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle } from 'lucide-react';

interface DangerousConfirmationModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    actionLabel: string;
    cancelText?: string;
    onConfirm: () => void | Promise<void>;
    onCancel: () => void;
    isProcessing?: boolean;
}

export const DangerousConfirmationModal: React.FC<DangerousConfirmationModalProps> = ({
    isOpen,
    title,
    message,
    actionLabel,
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    isProcessing = false,
}) => {
    const [typed, setTyped] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setTyped('');
            const timer = setTimeout(() => inputRef.current?.focus(), 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const matches = typed.trim() === actionLabel.trim();

    const handleConfirm = () => {
        if (!matches || isProcessing) return;
        onConfirm();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && matches && !isProcessing) {
            e.preventDefault();
            handleConfirm();
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[110]" onClose={() => !isProcessing && onCancel()}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-7 text-left border border-neutral-100">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="flex items-center justify-center w-11 h-11 bg-error-50 rounded-full">
                                        <AlertTriangle className="w-5 h-5 text-error-600" />
                                    </div>
                                    <Dialog.Title as="h3" className="text-base font-bold text-neutral-900">
                                        {title}
                                    </Dialog.Title>
                                </div>

                                <p className="text-sm text-neutral-600 leading-relaxed mb-4">
                                    {message}
                                </p>

                                <div className="mb-5">
                                    <label className="block text-xs font-medium text-neutral-600 mb-2">
                                        To confirm, type <span className="font-mono font-semibold text-neutral-900 bg-neutral-100 px-1.5 py-0.5 rounded">{actionLabel}</span> below.
                                    </label>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={typed}
                                        onChange={(e) => setTyped(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        disabled={isProcessing}
                                        placeholder={actionLabel}
                                        className="w-full text-sm border border-neutral-300 rounded-lg px-3 py-2 outline-none focus:border-error-500 focus:ring-1 focus:ring-error-500 disabled:bg-neutral-50 disabled:cursor-not-allowed transition-colors"
                                        autoComplete="off"
                                        spellCheck={false}
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={onCancel}
                                        disabled={isProcessing}
                                        className="flex-1 px-4 py-2.5 text-sm font-semibold text-neutral-600 bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {cancelText}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirm}
                                        disabled={!matches || isProcessing}
                                        className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-error-600 hover:bg-error-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-error-500 shadow-lg shadow-error-500/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                                    >
                                        {isProcessing ? 'Processing...' : actionLabel}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
